import React from 'react';
import Modal from '../ui/Modal';
import { Todo } from '../../types/todo';
import { useCategories } from '../../context/CategoriesContext';

interface TodoDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  todo: Todo | null;
  onToggle: (id: string) => void;
}

const TodoDetailModal: React.FC<TodoDetailModalProps> = ({ isOpen, onClose, todo, onToggle }) => {
  const { categories } = useCategories();

  if (!todo) return null;

  const priorityColors = {
    low: 'text-green-600 bg-green-100',
    medium: 'text-blue-600 bg-blue-100',
    high: 'text-orange-600 bg-orange-100',
    urgent: 'text-red-600 bg-red-100',
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (dateStr: string | null) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };


  const getCategoryDisplay = (categoryId: string) => {
    const category = categories.find((c: any) => c.id === categoryId);
    if (!category) return { name: categoryId, classes: 'bg-gray-100 text-gray-700' };

    const colorMap: { [key: string]: string } = {
      slate: 'bg-slate-500 text-white',
      gray: 'bg-gray-500 text-white',
      red: 'bg-red-500 text-white',
      orange: 'bg-orange-500 text-white',
      amber: 'bg-amber-500 text-white',
      yellow: 'bg-yellow-400 text-gray-900',
      lime: 'bg-lime-500 text-white',
      green: 'bg-green-500 text-white',
      emerald: 'bg-emerald-500 text-white',
      teal: 'bg-teal-500 text-white',
      cyan: 'bg-cyan-500 text-white',
      blue: 'bg-blue-500 text-white',
      indigo: 'bg-indigo-500 text-white',
      violet: 'bg-violet-500 text-white',
      purple: 'bg-purple-500 text-white',
      pink: 'bg-pink-500 text-white',
    };

    return {
      name: category.name,
      classes: colorMap[category.color] || 'bg-gray-100 text-gray-700'
    };
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Task Details">
      <div className="space-y-6 max-h-96 overflow-y-auto">
        {/* Header with title, status, and toggle */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  todo.completed ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
              <span className="text-sm font-medium text-gray-600">
                {todo.completed ? 'Completed' : 'Pending'}
              </span>
            </div>
            <button
              onClick={() => onToggle(todo._id)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                todo.completed
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {todo.completed ? 'Mark as Pending' : 'Mark as Complete'}
            </button>
          </div>
          <h2 className={`text-lg font-semibold ${
            todo.completed ? 'line-through text-gray-500' : 'text-gray-900'
          }`}>
            {todo.title}
          </h2>
        </div>

        {/* Category and Priority */}
        <div className="flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <span className={`inline-block px-2 py-1 text-xs rounded-md ${getCategoryDisplay(todo.category).classes}`}>
              {getCategoryDisplay(todo.category).name}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <span className={`inline-block px-2 py-1 text-xs rounded-md capitalize ${priorityColors[todo.priority]}`}>
              {todo.priority}
            </span>
          </div>
        </div>

        {/* Description */}
        {todo.description && todo.description.trim() !== '' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <div className="bg-gray-50 rounded-md p-3">
              <div
                className="text-sm text-gray-700 whitespace-pre-wrap break-words"
                dangerouslySetInnerHTML={{
                  __html: todo.description.replace(
                    /(https?:\/\/[^\s]+)/g,
                    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>'
                  )
                }}
              />
            </div>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Created Date
            </label>
            <p className="text-sm text-gray-600">
              {formatDate(todo.dates.created)}
            </p>
          </div>

          {todo.dates.received && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Received Date
              </label>
              <p className="text-sm text-gray-600">
                {formatDate(todo.dates.received)}
              </p>
            </div>
          )}

          {todo.dates.due && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <p className={`text-sm ${
                new Date(todo.dates.due) < new Date() && !todo.completed
                  ? 'text-red-600 font-medium'
                  : 'text-gray-600'
              }`}>
                {formatDateOnly(todo.dates.due)}
                {new Date(todo.dates.due) < new Date() && !todo.completed && (
                  <span className="ml-2 text-xs text-red-500">(Overdue)</span>
                )}
              </p>
            </div>
          )}

          {todo.dates.completed && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Completed Date
              </label>
              <p className="text-sm text-green-600">
                {formatDate(todo.dates.completed)}
              </p>
            </div>
          )}
        </div>

        {/* Email Details */}
        {todo.sender && todo.sender.email && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Details
            </label>
            <div className="bg-blue-50 rounded-md p-3 space-y-2">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                <span className="text-sm font-medium text-blue-900">From Email</span>
              </div>
              <div className="text-sm text-blue-800">
                <p className="font-medium">{todo.sender.name || 'Unknown Sender'}</p>
                <p className="text-blue-600">{todo.sender.email}</p>
              </div>
              {todo.gmailMessageId && (
                <p className="text-xs text-blue-500 font-mono">
                  ID: {todo.gmailMessageId}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tags */}
        {todo.tags && todo.tags.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <div className="flex flex-wrap gap-1">
              {todo.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-md"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Metadata
          </label>
          <div className="text-xs text-gray-500 space-y-1">
            <p>Last Updated: {formatDate(todo.updatedAt)}</p>
            <p>Task ID: {todo._id}</p>
            {todo.order !== undefined && <p>Order: {todo.order}</p>}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TodoDetailModal;